import type {CSSProperties, FocusEvent} from 'react';
import {useState} from 'react';

/**
 * /workstreams: show a control (e.g. an edit pencil) only while its
 * region is hovered or holds keyboard focus. Spread `regionProps` on the region
 * and `revealStyle` on the control's wrapper. The control fades rather than
 * unmounting, so anything it owns (e.g. its modal) survives the pointer leaving.
 * Mouse clicks inside the region don't count as focus, so it never sticks on.
 * A region that renders under a resting pointer gets no mouseenter, so any
 * move inside it counts too. While hidden, the control takes no pointer, so
 * hovering its empty spot doesn't open its tooltip.
 */
export const useHoverReveal = () => {
  const [hovered, setHovered] = useState(false);
  const [keyboardFocused, setKeyboardFocused] = useState(false);
  const revealed = hovered || keyboardFocused;

  const regionProps = {
    onMouseEnter: () => setHovered(true),
    onMouseMove: () => {
      if (!hovered) setHovered(true);
    },
    onMouseLeave: () => setHovered(false),
    onFocus: (event: FocusEvent<HTMLElement>) =>
      setKeyboardFocused(event.target.matches(':focus-visible')),
    onBlur: (event: FocusEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setKeyboardFocused(false);
      }
    },
  };
  const revealStyle: CSSProperties = {
    opacity: revealed ? 1 : 0,
    pointerEvents: revealed ? undefined : 'none',
    transition: 'opacity 120ms ease',
  };

  return {regionProps, revealStyle, revealed};
};
